import { AsyncPipe, UpperCasePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input, viewChildren } from '@angular/core';
import { takeUntilDestroyed, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Validators } from '@angular/forms';
import { FileType } from '@app/enums/FileTypes';
import { dateFormatPipe } from '@app/pipes/datepipe';
import { ReimbursementRequestService } from '@app/v2/services/reimbursement-request/reimbursement-request.service';
import { AlertComponent } from '@components/atoms/alert/alert.component';
import {
    MdsButtonComponent,
    MdsDatepickerComponent,
    MdsDividerComponent,
    MdsDropdownComponent,
    MdsTextFieldComponent,
} from '@medpacesoftwaredevelopment/designsystem';
import { Currency, CurrencyViewModel } from '@models/currency';
import { DateFormat } from '@models/date-format';
import { PaymentRequestStatus, ReimbursementRequest } from '@models/request';
import { CurrencyStateService } from '@services/state-management/currency-state.service';
import { RequestMerchantStateService } from '@services/state-management/request-merchant-state.service';
import { PersistentFormArray, PersistentFormControl, PersistentFormGroup } from '@utility/persistent-forms';
import { requiredAtLeastOneOptionArray } from '@utility/utility.validators';
import { getExtension } from 'mime';

import { LabeledTextComponent } from '@app/v2/components/labeled-text/labeled-text.component';
import { UserServiceV2 } from '@app/v2/services/user-service/user.service';
import { PatientStateService } from '@services/state-management/patient-state.service';
import moment from 'moment';
import { BehaviorSubject, combineLatest, filter, map, shareReplay, switchMap, tap } from 'rxjs';
import {
    PaymentRequestDeductionEditComponent,
    PaymentRequestDeductionFormGroup,
} from '../../payment-request-deduction/payment-request-deduction-edit/payment-request-deduction-edit.component';
import { ExpenseEditComponent } from '../expense/expense-edit/expense-edit.component';
import { ExpenseFormGroup } from '../expense/expense-edit/expense-edit.form';
@Component({
    selector: 'medpace-reimbursement-request-edit',
    imports: [
        MdsButtonComponent,
        MdsTextFieldComponent,
        AlertComponent,
        ExpenseEditComponent,
        dateFormatPipe,
        UpperCasePipe,
        AsyncPipe,
        MdsDatepickerComponent,
        MdsDropdownComponent,
        PaymentRequestDeductionEditComponent,
        MdsDividerComponent,
        LabeledTextComponent,
    ],
    templateUrl: './reimbursement-request-edit.component.html',
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ReimbursementRequestEditComponent {
    formGrp = input.required<ReimbursementRequestFormGroup>();
    formGrp$ = toObservable(this.formGrp);
    request = input<ReimbursementRequest>();
    requestMaybe$ = toObservable(this.request);
    requestNotNull$ = this.requestMaybe$.pipe(filter(Boolean));
    dateOnly = DateFormat.dateOnly;

    userService = inject(UserServiceV2);
    merchantStateService = inject(RequestMerchantStateService);
    requestService = inject(ReimbursementRequestService);
    currencyStateService = inject(CurrencyStateService);
    patientStateService = inject(PatientStateService);
    merchantTypes$ = this.merchantStateService.getPaymentRequestMerchants();
    currencyOptions$ = this.currencyStateService.getCurrenciesAsMdsOptions();
    elevatedAccess$ = this.userService.permissions.request.access.elevated$;

    expenseComponents = viewChildren(ExpenseEditComponent);

    isPreCurrencyConversionMigration = computed(() => {
        const request = this.request();
        if (!request) return undefined;

        return (
            request.totalAmountAuthorizedPatientsCurrency === null ||
            request.totalAmountAuthorizedPatientsCurrency === undefined ||
            request.totalAmountAuthorizedUsd === null ||
            request.totalAmountAuthorizedUsd === undefined
        );
    });

    private readonly totalCurrency$ = this.formGrp$.pipe(
        switchMap((fg) => fg.controls.totalCurrency.valueChangesReplay)
    );
    private readonly totalCurrencySignal = toSignal(this.totalCurrency$);
    private readonly patient = toSignal(this.patientStateService.getPatient());
    private readonly patientPreferredCurrency = computed(
        () => this.patient()?.preferredCurrency?.toUpperCase() ?? null
    );

    totalUsdAmount = computed((): { label: string; text: string } | null => {
        let sum = 0;
        let hasAny = false;
        for (const c of this.expenseComponents()) {
            const amounts = c.chipAmounts();
            const expenseCurrency = c.formGrp().controls.currency.value?.isocode?.toUpperCase();
            if (expenseCurrency === 'USD') {
                const authorized = parseFloat(
                    c.activeExpenseComponent()?.formGrp().controls.authorizedAmountInLocalCurrency.value
                );
                if (!isNaN(authorized)) {
                    sum += authorized;
                    hasAny = true;
                }
            } else {
                if (amounts?.usd != null) {
                    sum += amounts.usd;
                    hasAny = true;
                }
            }
        }
        if (!hasAny) return null;
        return { label: 'Total Authorized (USD)', text: `${sum.toFixed(2)}` };
    });

    totalPatientsCurrencyAmount = computed((): { label: string; text: string } | null => {
        const patientCurrency = this.patientPreferredCurrency();
        if (!patientCurrency) return null;
        let sum = 0;
        let hasAny = false;
        for (const c of this.expenseComponents()) {
            const amounts = c.chipAmounts();
            const expenseCurrency = c.formGrp().controls.currency.value?.isocode?.toUpperCase();
            if (expenseCurrency === patientCurrency) {
                const authorized = parseFloat(
                    c.activeExpenseComponent()?.formGrp().controls.authorizedAmountInLocalCurrency.value
                );
                if (!isNaN(authorized)) {
                    sum += authorized;
                    hasAny = true;
                }
            } else {
                if (amounts?.patientsCurrency != null) {
                    sum += amounts.patientsCurrency;
                    hasAny = true;
                }
            }
        }
        if (!hasAny) return null;
        return { label: `Total Authorized (${patientCurrency})`, text: `${sum.toFixed(2)}` };
    });
    push(request: ReimbursementRequest): ReimbursementRequest {
        const value = this.formGrp().getRawValue();
        request.reimbursementTransactions = this.expenseComponents().map((expenseComponent) => {
            return expenseComponent.push();
        });
        request.transactionDate = value.transactionDate
            ? moment(value.transactionDate).format('YYYY-MM-DDTHH:mm:ss')
            : null;
        request.merchant = value.merchant;
        const totalAuthorized = parseFloat(value.totalAuthorized);
        request.totalAmountAuthorized = Number.isNaN(totalAuthorized) ? null : totalAuthorized;
        request.totalCurrency = value.totalCurrency?.isocode;

        if (this.isVoidedSubject.getValue()) {
            // if is voided
            request.deducted = value.deduction.amount;
            request.deductedUsd = value.deduction.amountConverted;
            request.deductedCurrency = value.deduction.currency?.isocode;
        }

        const transactions = request.reimbursementTransactions;
        const usdVals = transactions.map((e) => e.authorizedAmountUsd).filter((v) => v != null);
        request.totalAmountAuthorizedUsd = usdVals.length > 0 ? usdVals.reduce((s, v) => s + v, 0) : null;
        const patVals = transactions.map((e) => e.authorizedAmountPatientsCurrency).filter((v) => v != null);
        request.totalAmountAuthorizedPatientsCurrency = patVals.length > 0 ? patVals.reduce((s, v) => s + v, 0) : null;

        return request;
    }
    patch$ = combineLatest({
        request: this.requestNotNull$,
        currencies: this.currencyStateService.getCurrencies(),
    })
        .pipe(
            tap(({ currencies, request }) => {
                this.formGrp().controls.expenses.clear();
                request.reimbursementTransactions?.forEach((expense) => {
                    this.formGrp().controls.expenses.push(new ExpenseFormGroup(expense));
                });
                const currency = currencies?.find((currency) => currency.isocode === request.totalCurrency);
                this.formGrp().patchValue({
                    merchant: request.merchant,
                    transactionDate: request.transactionDate ? new Date(request.transactionDate) : null,
                    totalAuthorized: request.totalAmountAuthorized?.toFixed(2) ?? null,
                    totalCurrency: currency,
                });
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    isVoidedSubject = new BehaviorSubject<boolean>(null);
    isVoided$ = this.requestMaybe$.pipe(
        map((request) => {
            if (!request) return null;
            return request.status?.statusId === PaymentRequestStatus.Voided;
        }),
        tap((isVoided) => {
            this.isVoidedSubject.next(isVoided);
        })
    );

    switchVoided$ = combineLatest({
        formGrp: this.formGrp$,
        isVoided: this.isVoided$,
    })
        .pipe(
            tap(({ formGrp, isVoided }) => {
                if (isVoided) {
                    formGrp.controls.deduction.enable({ emitEvent: true, force: true });
                } else {
                    formGrp.controls.deduction.disable({ emitEvent: true, persistent: true });
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();
    patchVoided$ = combineLatest({
        request: this.requestNotNull$,
        currencies: this.currencyStateService.getCurrencies(),
        isVoided: this.isVoided$,
    })
        .pipe(
            filter(({ isVoided }) => isVoided === true),
            tap(({ currencies, request }) => {
                const deductedCurrency = currencies?.find((currency) => currency.isocode === request.deductedCurrency);
                this.formGrp().patchValue({
                    deduction: {
                        amount: request.deducted,
                        amountConverted: request.deductedUsd,
                        currency: deductedCurrency,
                    },
                });
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    dateRequested$ = this.requestMaybe$.pipe(
        map((request) => request?.createdTimestamp?.toDate() ?? moment().toDate())
    );
    dateSubmitted$ = this.requestMaybe$.pipe(map((request) => request?.submittedTimestamp?.toDate() ?? null));

    public readonly acceptableFileTypes: string[] = [FileType.PDF, FileType.JPG, FileType.JPEG, FileType.PNG];
    public readonly accept = this.acceptableFileTypes.map((extensionWithoutDot) => '.' + extensionWithoutDot).join(',');

    onMultipleFileUpload(event: Event) {
        const input = event.target as HTMLInputElement;
        const files = Array.from({ length: input.files.length }, (_, index) => index)
            .map((index) => input.files.item(index))
            .filter((file) => this.acceptableFileTypes.includes(getExtension(file.type)));

        files.forEach((file) => {
            const formGrp = new ExpenseFormGroup();
            formGrp.controls.file.setValue(file, { emitEvent: true });
            formGrp.controls.file.markAsDirty();
            this.addExpense(formGrp);
        });
    }
    noExpensesWarning$ = this.formGrp$.pipe(
        switchMap((formGrp) =>
            formGrp.controls.expenses.statusChanges.pipe(
                map(() => {
                    const expensesControl = formGrp.controls.expenses;
                    if (expensesControl.touched && expensesControl.hasError('required')) {
                        return expensesControl.getError('required') as string;
                    }
                    return undefined;
                })
            )
        )
    );

    isInProcessOrHigherAndHasElevatedAccess$ = combineLatest({
        elevatedAccess: this.elevatedAccess$,
        isInProcessOrHigher: this.requestService.isInProcessOrHigher$,
    }).pipe(
        map(({ elevatedAccess, isInProcessOrHigher }) => {
            return elevatedAccess && isInProcessOrHigher;
        }),
        shareReplay(1)
    );

    expenses$ = this.formGrp$.pipe(
        switchMap((formGrp) => formGrp.controls.expenses.valueChanges),
        shareReplay(1)
    );
    initializePreferredCurrencyEffect$ = combineLatest({
        request: this.requestMaybe$,
        patient: this.patientStateService.getPatient(),
        currencies: this.currencyStateService.getCurrencies(),
        currencyUSD: this.currencyStateService.getCurrencyUSD(),
        formGrp: this.formGrp$,
    })
        .pipe(
            filter(({ request }) => !request), // expense hasnt emitted yet
            tap(({ currencies, currencyUSD, patient, formGrp }) => {
                let currency: CurrencyViewModel | null = null;
                if (patient?.preferredCurrency) {
                    currency = currencies.find((currency) => currency.isocode === patient.preferredCurrency);
                }
                if (!currency) currency = currencyUSD;

                if (currency) formGrp.controls.totalCurrency.setValue(currency);
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    private readonly matchingCurrencies$ = combineLatest({
        totalCurrency: this.formGrp$.pipe(switchMap((formGrp) => formGrp.controls.totalCurrency.valueChanges)),
        expenseCurrencies: this.expenses$.pipe(
            map((expenses) => {
                return expenses.map((e) => e.currency);
            })
        ),
    }).pipe(
        map(({ expenseCurrencies, totalCurrency }) => {
            // all currencies equal or some expense currencies are empty
            return expenseCurrencies.every((currency) => currency === totalCurrency || currency === null);
        }),
        shareReplay(1)
    );
    private readonly totalRequested$ = this.expenses$.pipe(
        map((expenses) => {
            return expenses
                .filter((expense) => !!expense.currency)
                .reduce((prev, curr) => {
                    const requestedString = curr.otherExpense
                        ? curr.otherExpense.authorizedAmountInLocalCurrency
                        : curr.mileageExpense
                          ? curr.mileageExpense.authorizedAmountInLocalCurrency
                          : undefined;

                    const requested =
                        requestedString != null && requestedString != undefined && requestedString !== ''
                            ? (Number.parseFloat(requestedString as string) ?? 0)
                            : 0; // in case it's NaN
                    return prev + requested;
                }, 0);
        }),
        shareReplay(1)
    );
    private readonly setTotalRequested$ = combineLatest({
        isInProcessOrHigherAndHasElevatedAccess: this.isInProcessOrHigherAndHasElevatedAccess$,
        matchingCurrencies: this.matchingCurrencies$,
        totalRequested: this.totalRequested$,
        request: this.requestNotNull$,
    })
        .pipe(
            filter(({ isInProcessOrHigherAndHasElevatedAccess }) => {
                return isInProcessOrHigherAndHasElevatedAccess === true;
            }),
            tap(({ request, matchingCurrencies, totalRequested }) => {
                if (matchingCurrencies) {
                    this.formGrp().controls.totalAuthorized.setValue(totalRequested.toFixed(2));
                } else {
                    const value =
                        request.totalAmountAuthorized != null && request.totalAmountAuthorized !== undefined
                            ? request.totalAmountAuthorized
                            : null;
                    this.formGrp().controls.totalAuthorized.setValue(value?.toFixed(2) ?? null);
                }
            }),
            takeUntilDestroyed()
        )
        .subscribe();

    protected onAddExpenseClick() {
        this.addExpense(new ExpenseFormGroup());
    }
    private addExpense(formGrp: ExpenseFormGroup) {
        this.formGrp().controls.expenses.push(formGrp, { emitEvent: true });
    }
    removeExpense(expenseIndex: number) {
        this.formGrp().controls.expenses.removeAt(expenseIndex);
    }
}
export class ReimbursementRequestFormGroup extends PersistentFormGroup<{
    expenses: PersistentFormArray<ExpenseFormGroup>;
    transactionDate: PersistentFormControl<Date>;
    merchant: PersistentFormControl<number>;
    totalAuthorized: PersistentFormControl<string>;
    totalCurrency: PersistentFormControl<Currency>;
    deduction: PaymentRequestDeductionFormGroup;
}> {
    constructor() {
        super({
            expenses: new PersistentFormArray<ExpenseFormGroup>([], requiredAtLeastOneOptionArray()),
            transactionDate: new PersistentFormControl<Date>(null),
            merchant: new PersistentFormControl<number>(0),
            totalAuthorized: new PersistentFormControl<string>(null, [Validators.min(-1e6), Validators.max(1e6)]),
            totalCurrency: new PersistentFormControl<Currency>(null, [Validators.required]),
            deduction: new PaymentRequestDeductionFormGroup(),
        });
    }
}
